import { useToast } from 'primevue/usetoast';
import {
  useGetMyRoleRequestsQuery,
  useApproveRoleRequestsMutation,
  useRejectRoleRequestsMutation,
  useWithdrawRoleRequestMutation,
} from '@/types/vue-apollo-helpers';

export function useMyRoleRequests() {
  const toast = useToast();
  const search = ref('');

  const { result, loading, refetch } = useGetMyRoleRequestsQuery();

  const { mutate: approveMutate } = useApproveRoleRequestsMutation();
  const { mutate: rejectMutate } = useRejectRoleRequestsMutation();
  const { mutate: withdrawMutate } = useWithdrawRoleRequestMutation();

  const roleRequests = computed(() => {
    const data = result.value?.myRoleRequests ?? [];

    if (!search.value) return data;

    const q = search.value.toLowerCase();
    return data.filter((r) =>
      r.permissionGroup?.name?.toLowerCase().includes(q) ||
      r.category?.toLowerCase().includes(q) ||
      r.scope_description?.toLowerCase().includes(q) ||
      r.status?.toLowerCase().includes(q),
    );
  });

  const approve = async(id: string) => {
    try {
      await approveMutate({
        input: { ids: [id] },
      });

      toast.add({
        severity: 'success',
        summary: 'Approved',
        detail: 'Request approved successfully',
        life: 3000,
      });

      refetch();
    } catch (err) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: err instanceof Error ? err.message : 'Failed to approve request',
        life: 4000,
      });
    }
  };

  const reject = async(id: string, reason: string) => {
    try {
      await rejectMutate({
        input: { ids: [id], reason },
      });

      toast.add({
        severity: 'warn',
        summary: 'Rejected',
        detail: 'Request rejected',
        life: 3000,
      });

      refetch();
    } catch (err) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: err instanceof Error ? err.message : 'Failed to reject request',
        life: 4000,
      });
    }
  };

  const withdraw = async(id: string) => {
    try {
      await withdrawMutate({ id });

      toast.add({
        severity: 'info',
        summary: 'Withdrawn',
        detail: 'Request withdrawn',
        life: 3000,
      });

      refetch();
    } catch (err) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: err instanceof Error ? err.message : 'Failed to withdraw request',
        life: 4000,
      });
    }
  };

  return {
    roleRequests,
    loading,
    search,
    refetch,
    approve,
    reject,
    withdraw,
  };
}
